import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListGrowerPaymentsParams,
    UpsertGrowerPaymentRequest,
} from '@/lib/api/grower-payment-api'
import { growerPaymentApi } from '@/lib/api/grower-payment-api'
import { m } from '@/lib/paraglide/messages'

export const growerPaymentKeys = {
    all: ['grower-payments'] as const,
    lists: () => [...growerPaymentKeys.all, 'list'] as const,
    list: (params: ListGrowerPaymentsParams) =>
        [...growerPaymentKeys.lists(), params] as const,
    summary: (params: ListGrowerPaymentsParams) =>
        [...growerPaymentKeys.all, 'summary', params] as const,
}

export function useGrowerPayments(
    params?: ListGrowerPaymentsParams,
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: growerPaymentKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await growerPaymentApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
        // Giữ dữ liệu cũ khi đổi trang/bộ lọc để bảng không nháy sang rỗng.
        placeholderData: keepPreviousData,
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" — gom page 1..N. `enabled` false khi
 * đang ở desktop để không fetch trùng với query phân trang.
 */
export function useGrowerPaymentsInfinite(
    params: Omit<ListGrowerPaymentsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...growerPaymentKeys.all, 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            growerPaymentApi.list({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

// Tổng tiền tính trên TOÀN BỘ bộ lọc nên là query riêng, không suy từ trang hiện tại.
export function useGrowerPaymentSummary(
    params?: ListGrowerPaymentsParams,
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: growerPaymentKeys.summary(params ?? {}),
        queryFn: async () => {
            const res = await growerPaymentApi.summary(params)
            return res.data ?? { total_amount: 0, count: 0 }
        },
        placeholderData: keepPreviousData,
    })
}

function useInvalidate() {
    const qc = useQueryClient()
    return () => void qc.invalidateQueries({ queryKey: growerPaymentKeys.all })
}

export function useCreateGrowerPayment() {
    const invalidate = useInvalidate()
    return useMutation({
        mutationFn: (data: UpsertGrowerPaymentRequest) =>
            growerPaymentApi.create(data),
        onSuccess: () => {
            invalidate()
            toast.success(m.toast_grower_payment_save_success())
        },
        onError: () => toast.error(m.toast_grower_payment_save_error()),
    })
}

export function useUpdateGrowerPayment(id: string) {
    const invalidate = useInvalidate()
    return useMutation({
        mutationFn: (data: UpsertGrowerPaymentRequest) =>
            growerPaymentApi.update(id, data),
        onSuccess: () => {
            invalidate()
            toast.success(m.toast_grower_payment_save_success())
        },
        onError: () => toast.error(m.toast_grower_payment_save_error()),
    })
}

export function useDeleteGrowerPayment() {
    const invalidate = useInvalidate()
    return useMutation({
        mutationFn: (id: string) => growerPaymentApi.delete(id),
        onSuccess: () => {
            invalidate()
            toast.success(m.toast_grower_payment_delete_success())
        },
        onError: () => toast.error(m.toast_grower_payment_delete_error()),
    })
}
